import React, { useEffect, useMemo, useState } from 'react'
import CustomRow from '../../../../reusable-components/CustomRow'
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  Input,
  message,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Tabs,
  Typography,
  theme
} from 'antd'
import { useSelector } from 'react-redux'
import Templates from '../../utils/templates'
import useGeneralForm from '../../hooks/useGeneralForm'
import { faInfoCircle, faKey, faUser, faUserDoctor, faXmark, faPlus } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { hexToRGBA, specialFieldValidation } from '../../../../lib/utils'
import RenderFormItems from './RenderFormItems'
import { readProfessionalTypes } from '../../../../../core/utils/utils'

const { Title, Text } = Typography

const SystemUsersGerneral = React.memo(
  ({
    type,
    isNewRecord,
    activeRecords,
    dataIndex,
    setViewType,
    refreshView,
    entities,
    services,
    servicesLoading,
    closeTab
  }) => {
    const authState = useSelector((state) => state.auth)
    const { token } = theme.useToken()
    const [formChangeCounter, setFormChangeCounter] = useState(0)
    const [professionalTypes, setProfessionalTypes] = useState([])

    useEffect(() => {
      fetchProfessionalTypes()
    }, [])

    const fetchProfessionalTypes = async () => {
      try {
        const tmpProfessionalTypes = await readProfessionalTypes()
        setProfessionalTypes(tmpProfessionalTypes)
      } catch (error) {
        console.error('Error fetching professional types:', error)
      }
    }

    const {
      handleIsActive,
      handleRolesSelection,
      handleSubmit,
      setSpecialFormState,
      deactivatePrompt,
      clientForm,
      loading,
      specialFormState,
      unfilledMedicalFields,
      unfilledProfileFields,
      record,
      handleFieldsChange
    } = useGeneralForm({
      isNewRecord,
      activeRecords,
      dataIndex,
      refreshView,
      type,
      setViewType,
      closeTab
    })

    // Watch form values to trigger button state updates
    const watchedValues = Form.useWatch([], clientForm)

    useEffect(() => {
      handleFieldsChange()
      setFormChangeCounter((prev) => prev + 1)
      // eslint-disable-next-line
    }, [watchedValues])

    // Get the appropriate title based on user type
    const getTitleInfo = () => {
      switch (type) {
        case 'medical':
          return {
            title: isNewRecord ? 'Add Medical Professional' : 'Edit Medical Professional',
            subtitle: isNewRecord
              ? 'Create a new medical professional profile'
              : 'Update medical professional information'
          }
        case 'patient':
          return {
            title: isNewRecord ? 'Add Patient' : 'Edit Patient',
            subtitle: isNewRecord ? 'Create a new patient profile' : 'Update patient information'
          }
        default:
          return {
            title: isNewRecord ? 'Add System User' : 'Edit System User',
            subtitle: isNewRecord ? 'Create a new system user account' : 'Update system user information'
          }
      }
    }

    const titleInfo = getTitleInfo()

    // Check if all required fields are filled
    const isFormValid = useMemo(() => {
      if (!isNewRecord) return true // Always allow saving for existing records

      try {
        const formValues = clientForm.getFieldsValue()

        // Basic required fields
        const hasFirstName = formValues.firstName?.trim()
        const hasLastName = formValues.lastName?.trim()
        const hasEmail = formValues.email?.trim()
        const hasPhoneNumber = formValues.phoneNumber?.trim()
        const hasRole = formValues.extraData?.role?.type

        // Role-specific requirements
        let roleSpecificValid = true

        if (formValues.extraData?.role?.type === 'medical_professional' || type === 'medical') {
          const hasClinics = formValues.extraData?.clinics?.length > 0
          const hasProfession = formValues.extraData?.profession
          const hasServices = formValues.extraData?.services?.length > 0

          roleSpecificValid = hasClinics && hasProfession && hasServices

          // Additional check for doctors
          if (formValues.extraData?.profession === 'doctor') {
            const hasMpNumber = formValues.extraData?.mpNumber?.trim()
            const hasAmpathId = formValues.extraData?.ampathId?.trim()
            const hasQualification = formValues.extraData?.qualification?.trim()
            roleSpecificValid = roleSpecificValid && hasMpNumber && hasQualification && hasAmpathId
          }
        }

        if (formValues.extraData?.role?.type === 'reception') {
          const hasClinics = formValues.extraData?.clinics?.length > 0
          roleSpecificValid = hasClinics
        }

        const isValid = hasFirstName && hasLastName && hasEmail && hasPhoneNumber && hasRole && roleSpecificValid

        return isValid
      } catch (error) {
        return false
      }
    }, [clientForm, isNewRecord, type, formChangeCounter, watchedValues])

    return (
      <>
        {/* Enhanced Header */}
        <Card
          style={{
            marginBottom: '24px',
            borderRadius: '16px',
            border: `1px solid ${hexToRGBA(token.colorBorder, 0.1)}`,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
          }}
          bodyStyle={{ padding: '20px 32px' }}
        >
          <Row justify='space-between' align='middle'>
            <Col>
              <Space size={16}>
                <div
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    backgroundColor: hexToRGBA(token.colorPrimary, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <FontAwesomeIcon icon={faPlus} style={{ color: token.colorPrimary, fontSize: '20px' }} />
                </div>
                <div>
                  <Title level={3} style={{ margin: 0, color: token.colorPrimary }}>
                    {titleInfo.title}
                  </Title>
                  <Text type='secondary' style={{ fontSize: '16px' }}>
                    {titleInfo.subtitle}
                  </Text>
                </div>
              </Space>
            </Col>
            <Col>
              <Space size={12}>
                <Button
                  onClick={closeTab}
                  style={{
                    borderRadius: '6px',
                    height: '40px',
                    fontWeight: '500'
                  }}
                >
                  <FontAwesomeIcon icon={faXmark} style={{ marginRight: '8px' }} />
                  Cancel
                </Button>
                {(authState.agiliteUser.extraData.role.type === 'admin' ||
                  authState.agiliteUser.extraData.role.type === 'super_admin') && (
                  <Form.Item noStyle>
                    {deactivatePrompt ? (
                      <Popconfirm
                        okText='Yes'
                        cancelText='No'
                        okButtonProps={{
                          style: { background: token.colorSuccess, color: 'white', fontWeight: '500' }
                        }}
                        cancelButtonProps={{
                          style: { background: token.colorError, color: 'white', fontWeight: '500' }
                        }}
                        title={<h3 style={{ color: token.colorWarning, margin: 0 }}>WARNING</h3>}
                        description='You have switched this system user account to be inactive. Are you sure you want to proceed?'
                        onConfirm={() => {
                          clientForm.submit()
                        }}
                      >
                        <Button
                          type='primary'
                          loading={loading}
                          disabled={isNewRecord && !isFormValid}
                          onClick={() => {
                            if (!deactivatePrompt) {
                              clientForm.submit()
                            }
                          }}
                          style={{
                            background: isNewRecord && !isFormValid ? undefined : token.actionColor,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            height: '40px',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                            borderRadius: '6px'
                          }}
                          icon={<FontAwesomeIcon icon={faPlus} />}
                        >
                          {isNewRecord ? 'Create Profile' : 'Save Changes'}
                        </Button>
                      </Popconfirm>
                    ) : (
                      <Button
                        type='primary'
                        loading={loading}
                        disabled={isNewRecord && !isFormValid}
                        onClick={() => {
                          clientForm.submit()
                        }}
                        style={{
                          background: isNewRecord && !isFormValid ? undefined : token.actionColor,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          height: '40px',
                          boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                          borderRadius: '6px'
                        }}
                        size='large'
                        icon={<FontAwesomeIcon icon={faPlus} />}
                      >
                        {isNewRecord ? 'Create Profile' : 'Save Changes'}
                      </Button>
                    )}
                  </Form.Item>
                )}
              </Space>
            </Col>
          </Row>
        </Card>

        <CustomRow justify='center' className='basic-card'>
          <Col span={24}>
            <Form
              id='medProf_form'
              style={{ maxWidth: '100%', margin: '0 auto' }}
              onFinish={handleSubmit}
              onFinishFailed={(e) => {
                message.error('Required fields are incomplete. Please fill in all mandatory fields and submit again.')
              }}
              name='form'
              form={clientForm}
              onFieldsChange={(changedFields, allFields) => {
                handleFieldsChange()
                setFormChangeCounter((prev) => prev + 1)
              }}
              initialValues={{
                ...Templates.dataTemplate(),
                ...activeRecords[dataIndex],
                extraData: {
                  ...Templates.dataTemplate().extraData,
                  ...activeRecords[dataIndex]?.extraData,
                  services: activeRecords[dataIndex]?.extraData?.services || [],
                  profession: activeRecords[dataIndex]?.extraData?.profession || ''
                },
                phoneNumber: activeRecords[dataIndex]?.phoneNumber
                  ? activeRecords[dataIndex]?.phoneNumber.replace('+27', '')
                  : ''
              }}
              layout='vertical'
            >
              <Tabs
                hideAdd
                size='large'
                style={{
                  background: 'white',
                  borderRadius: '16px',
                  padding: '0',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                }}
                tabBarStyle={{
                  padding: '0 32px',
                  margin: 0,
                  background: hexToRGBA(token.colorBgContainer, 0.5),
                  borderRadius: '16px 16px 0 0'
                }}
              >
                <Tabs.TabPane
                  tab={
                    <span style={{ fontWeight: '500', fontSize: '16px' }}>
                      <FontAwesomeIcon icon={faUser} style={{ marginRight: '8px', color: token.colorPrimary }} />
                      Profile Details
                    </span>
                  }
                  key='general'
                >
                  <div style={{ padding: '32px' }}>
                    {authState.agiliteUser.extraData.role.type === 'super_admin' && (
                      <Card
                        style={{
                          marginBottom: '32px',
                          borderRadius: '12px',
                          border: `1px solid ${hexToRGBA(token.colorWarning, 0.2)}`,
                          background: hexToRGBA(token.colorWarning, 0.02)
                        }}
                        bodyStyle={{ padding: '20px' }}
                      >
                        <Form.Item
                          name={Templates.dataModel.entities.key}
                          label={
                            <Text strong style={{ fontSize: '15px' }}>
                              Linked Entities
                            </Text>
                          }
                          rules={[
                            {
                              required: Templates.dataModel.entities.required,
                              message: 'Select Admin linked entities'
                            }
                          ]}
                        >
                          <Select
                            mode='multiple'
                            size='large'
                            placeholder='Select entities'
                            style={{ borderRadius: '8px' }}
                            filterOption={(input, option) => {
                              return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }}
                            options={entities.map((entity) => ({
                              label: entity.name,
                              value: entity._id
                            }))}
                          />
                        </Form.Item>
                      </Card>
                    )}

                    <RenderFormItems
                      activeRecord={activeRecords[dataIndex]}
                      isNewRecord={isNewRecord}
                      clientForm={clientForm}
                      specialFormState={specialFormState}
                      setSpecialFormState={setSpecialFormState}
                      handleIsActive={handleIsActive}
                      handleRolesSelection={handleRolesSelection}
                      type={type}
                      record={record}
                    />
                  </div>
                </Tabs.TabPane>

                {(activeRecords[dataIndex]?.extraData.role.type === 'medical_professional' || type === 'medical') && (
                  <Tabs.TabPane
                    tab={
                      <span style={{ fontWeight: '500', fontSize: '16px' }}>
                        <FontAwesomeIcon
                          icon={faUserDoctor}
                          style={{ marginRight: '8px', color: token.colorSecondary }}
                        />
                        Medical Profile
                        {unfilledMedicalFields > 0 && (
                          <Badge
                            count={unfilledMedicalFields}
                            style={{ backgroundColor: token.colorError, marginLeft: 8 }}
                          />
                        )}
                      </span>
                    }
                    key='professional-profile'
                  >
                    <div style={{ padding: '32px' }}>
                      <Row gutter={[32, 24]}>
                        <Col xs={24} lg={12}>
                          <Card
                            title={
                              <Space size={12}>
                                <div
                                  style={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '10px',
                                    backgroundColor: hexToRGBA(token.colorSecondary, 0.1),
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <FontAwesomeIcon
                                    icon={faInfoCircle}
                                    style={{ color: token.colorSecondary, fontSize: '18px' }}
                                  />
                                </div>
                                <div>
                                  <Text strong style={{ fontSize: '18px', display: 'block' }}>
                                    Professional Information
                                  </Text>
                                  <Text type='secondary' style={{ fontSize: '14px' }}>
                                    Medical profession and qualification details
                                  </Text>
                                </div>
                              </Space>
                            }
                            bordered={false}
                            style={{
                              borderRadius: '16px',
                              boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                              background: 'linear-gradient(135deg, #ffffff 0%, #fff8f0 100%)',
                              border: `1px solid ${hexToRGBA(token.colorBorder, 0.1)}`,
                              height: '100%'
                            }}
                            bodyStyle={{ padding: '32px' }}
                            headStyle={{
                              borderBottom: 'none',
                              backgroundColor: 'transparent',
                              padding: '24px 32px 0 32px'
                            }}
                          >
                            <Space direction='vertical' size={24} style={{ width: '100%' }}>
                              <Form.Item
                                name={Templates.dataModel.professionalType.key}
                                label={
                                  <Text strong style={{ fontSize: '15px' }}>
                                    {Templates.dataModel.professionalType.label}
                                  </Text>
                                }
                                rules={[
                                  {
                                    required: Templates.dataModel.professionalType.required,
                                    message: Templates.dataModel.professionalType.validationMsg
                                  }
                                ]}
                              >
                                <Select
                                  size='large'
                                  placeholder='Select a professional type'
                                  style={{ borderRadius: '8px' }}
                                  options={[
                                    { value: '', label: '-Select a professional type-' },
                                    ...professionalTypes.map((type) => ({
                                      value: type.value,
                                      label: type.label
                                    }))
                                  ]}
                                />
                              </Form.Item>
                              <Form.Item
                                name={Templates.dataModel.profession.key}
                                label={
                                  <Text strong style={{ fontSize: '15px' }}>
                                    {Templates.dataModel.profession.label}
                                  </Text>
                                }
                                rules={[
                                  {
                                    required: Templates.dataModel.profession.required,
                                    message: Templates.dataModel.profession.validationMsg
                                  }
                                ]}
                              >
                                <Select
                                  size='large'
                                  placeholder='Select a profession'
                                  style={{ borderRadius: '8px' }}
                                  options={[{ value: '', label: '-Select a profession-' }, ...authState.professions]}
                                />
                              </Form.Item>

                              {(activeRecords[dataIndex]?.extraData.profession === 'doctor' ||
                                record?.extraData?.profession === 'doctor') &&
                                (record?.extraData?.profession === 'doctor' || !record?.extraData?.profession) && (
                                  <>
                                    <Form.Item
                                      name={Templates.dataModel.mpNumber.key}
                                      label={
                                        <Text strong style={{ fontSize: '15px' }}>
                                          {Templates.dataModel.mpNumber.label}
                                        </Text>
                                      }
                                      rules={[
                                        {
                                          required: Templates.dataModel.mpNumber.required,
                                          message: Templates.dataModel.mpNumber.validationMsg
                                        }
                                      ]}
                                    >
                                      <Input
                                        size='large'
                                        style={{ borderRadius: '8px' }}
                                        onChange={(e) => {
                                          specialFieldValidation(clientForm, specialFormState, setSpecialFormState, {
                                            key: 'extraData.mpNumber',
                                            event: e,
                                            validationConfig: {
                                              letters: { allowed: false, onlyCaps: false },
                                              numbers: true,
                                              spaces: false
                                            }
                                          })
                                        }}
                                        maxLength={50}
                                        placeholder={Templates.dataModel.mpNumber.placeholder}
                                      />
                                    </Form.Item>

                                    <Form.Item
                                      name={Templates.dataModel.ampathId.key}
                                      label={
                                        <Text strong style={{ fontSize: '15px' }}>
                                          {Templates.dataModel.ampathId.label}
                                        </Text>
                                      }
                                      rules={[
                                        {
                                          required: Templates.dataModel.ampathId.required,
                                          message: Templates.dataModel.ampathId.validationMsg
                                        }
                                      ]}
                                    >
                                      <Input
                                        size='large'
                                        style={{ borderRadius: '8px' }}
                                        onChange={(e) => {
                                          specialFieldValidation(clientForm, specialFormState, setSpecialFormState, {
                                            key: 'extraData.ampathId',
                                            event: e,
                                            validationConfig: {
                                              letters: { allowed: false, onlyCaps: false },
                                              numbers: true,
                                              spaces: false
                                            }
                                          })
                                        }}
                                        maxLength={50}
                                        placeholder={Templates.dataModel.ampathId.placeholder}
                                      />
                                    </Form.Item>

                                    <Form.Item
                                      name={Templates.dataModel.qualification.key}
                                      label={
                                        <Text strong style={{ fontSize: '15px' }}>
                                          {Templates.dataModel.qualification.label}
                                        </Text>
                                      }
                                      rules={[
                                        {
                                          required: Templates.dataModel.qualification.required,
                                          message: Templates.dataModel.qualification.validationMsg
                                        }
                                      ]}
                                    >
                                      <Input
                                        size='large'
                                        style={{ borderRadius: '8px' }}
                                        placeholder={Templates.dataModel.qualification.placeholder}
                                      />
                                    </Form.Item>
                                  </>
                                )}

                              <Form.Item
                                name={Templates.dataModel.services.key}
                                label={
                                  <Space>
                                    <Text strong style={{ fontSize: '15px' }}>
                                      Services
                                    </Text>
                                    <Text style={{ color: token.colorError }}>*</Text>
                                  </Space>
                                }
                              >
                                <Select
                                  loading={servicesLoading}
                                  size='large'
                                  placeholder='Select services'
                                  mode='multiple'
                                  showSearch
                                  style={{ borderRadius: '8px' }}
                                  optionFilterProp='label'
                                  options={services}
                                />
                              </Form.Item>
                            </Space>
                          </Card>
                        </Col>

                        <Col xs={24} lg={12}>
                          <Card
                            title={
                              <Space size={12}>
                                <div
                                  style={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '10px',
                                    backgroundColor: hexToRGBA(token.colorInfo, 0.1),
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <FontAwesomeIcon icon={faKey} style={{ color: token.colorInfo, fontSize: '18px' }} />
                                </div>
                                <div>
                                  <Text strong style={{ fontSize: '18px', display: 'block' }}>
                                    Access & Permissions
                                  </Text>
                                  <Text type='secondary' style={{ fontSize: '14px' }}>
                                    System access and management permissions
                                  </Text>
                                </div>
                              </Space>
                            }
                            bordered={false}
                            style={{
                              borderRadius: '16px',
                              boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                              background: 'linear-gradient(135deg, #ffffff 0%, #f0f8ff 100%)',
                              border: `1px solid ${hexToRGBA(token.colorBorder, 0.1)}`,
                              height: '100%'
                            }}
                            bodyStyle={{ padding: '32px' }}
                            headStyle={{
                              borderBottom: 'none',
                              backgroundColor: 'transparent',
                              padding: '24px 32px 0 32px'
                            }}
                          >
                            <Space direction='vertical' size={32} style={{ width: '100%' }}>
                              <Card
                                size='small'
                                style={{
                                  borderRadius: '12px',
                                  border: `2px solid ${hexToRGBA(token.colorSuccess, 0.15)}`,
                                  backgroundColor: hexToRGBA(token.colorSuccess, 0.04),
                                  textAlign: 'center'
                                }}
                                bodyStyle={{ padding: '24px' }}
                              >
                                <Text strong style={{ display: 'block', marginBottom: '16px', fontSize: '15px' }}>
                                  Virtual Services
                                </Text>

                                <Space>
                                  <Text strong>Offers Virtual Service</Text>
                                  <Form.Item
                                    name={['extraData', 'offersVirtualService']}
                                    valuePropName='checked'
                                    style={{ margin: 0 }}
                                  >
                                    <Switch checkedChildren='Yes' unCheckedChildren='No' />
                                  </Form.Item>
                                </Space>
                              </Card>
                              <Card
                                size='small'
                                style={{
                                  borderRadius: '12px',
                                  border: `2px solid ${hexToRGBA(token.colorInfo, 0.15)}`,
                                  backgroundColor: hexToRGBA(token.colorInfo, 0.04),
                                  textAlign: 'center'
                                }}
                                bodyStyle={{ padding: '24px' }}
                              >
                                <Text strong style={{ display: 'block', marginBottom: '16px', fontSize: '15px' }}>
                                  Availability Access
                                </Text>
                                <Space>
                                  <Text strong>Has Access to Availability</Text>
                                  <Form.Item
                                    name={['extraData', 'hasAvailabilityAccess']}
                                    valuePropName='checked'
                                    style={{ margin: 0 }}
                                  >
                                    <Switch checkedChildren='Yes' unCheckedChildren='No' />
                                  </Form.Item>
                                </Space>
                              </Card>

                              <Card
                                size='small'
                                style={{
                                  borderRadius: '12px',
                                  border: `2px solid ${hexToRGBA(token.colorWarning, 0.15)}`,
                                  backgroundColor: hexToRGBA(token.colorWarning, 0.04),
                                  textAlign: 'center'
                                }}
                                bodyStyle={{ padding: '24px' }}
                              >
                                <Text strong style={{ display: 'block', marginBottom: '16px', fontSize: '15px' }}>
                                  Management Role
                                </Text>
                                <Space>
                                  <Text strong>{Templates.dataModel.isManager.label}</Text>
                                  <Form.Item
                                    name={Templates.dataModel.isManager.key}
                                    valuePropName='checked'
                                    rules={[
                                      {
                                        required: Templates.dataModel.isManager.required,
                                        message: Templates.dataModel.isManager.validationMsg
                                      }
                                    ]}
                                    style={{ margin: 0 }}
                                  >
                                    <Switch checkedChildren='Yes' unCheckedChildren='No' />
                                  </Form.Item>
                                </Space>
                              </Card>
                            </Space>
                          </Card>
                        </Col>
                      </Row>
                    </div>
                  </Tabs.TabPane>
                )}
              </Tabs>
            </Form>
          </Col>
        </CustomRow>
      </>
    )
  }
)

export default SystemUsersGerneral
